import { useState } from 'react';
import {
  CATALOG_VERSION_STATUS_LABELS,
  GEO_SCOPE_TYPE_LABELS,
  TARIFF_VARIATION_TYPE_LABELS,
  TariffVariationTypeSchema,
  type CatalogVersionDto,
  type CostCatalogDto,
  type DuplicationLineDto,
} from '../../lib/api-types';
import type { BulkAssignmentInput, DuplicationInput, UnitCostCreateInput } from '../../lib/api';
import { toDisplayError } from '../../lib/errors';
import { Alert } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Dialog } from '../../components/ui/Dialog';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Select } from '../../components/ui/Select';
import { Spinner } from '../../components/ui/Spinner';
import {
  useApplyDuplication,
  useCatalogVersions,
  useComponentTypes,
  useCostCatalogs,
  useCreateCatalogVersion,
  useCreateCostCatalog,
  useCreateUnitCost,
  usePreviewDuplication,
  useReferentialMeasurementUnits,
  useTransitionCatalogVersion,
  useUnitCosts,
} from './hooks';
import {
  CodeNameForm,
  EMPTY_SCOPE,
  LifecycleButtons,
  ScopePicker,
  type ScopeValue,
} from './shared';

const badgeToneForStatus = (status: string): 'neutral' | 'success' | 'warning' | 'danger' => {
  if (status === 'ACTIF') return 'success';
  if (status === 'EN_VALIDATION') return 'warning';
  if (status === 'ARCHIVE' || status === 'EXPIRE') return 'danger';
  return 'neutral';
};

/** « Catalogues » : catalogues → versions → coûts unitaires, cycle de vie et duplication. */
export function CataloguesTab(): JSX.Element {
  const catalogsQuery = useCostCatalogs();
  const createCatalog = useCreateCostCatalog();
  const [creating, setCreating] = useState(false);
  const [selectedCatalogId, setSelectedCatalogId] = useState('');

  const catalogs = catalogsQuery.data ?? [];
  const selectedCatalog = catalogs.find((c) => c.id === selectedCatalogId) ?? null;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle>Catalogues de coûts</CardTitle>
            <Button
              size="sm"
              onClick={() => {
                createCatalog.reset();
                setCreating(true);
              }}
            >
              Nouveau catalogue
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {catalogsQuery.isLoading ? (
            <Spinner label="Chargement des catalogues…" />
          ) : catalogsQuery.isError ? (
            <Alert variant="error">{toDisplayError(catalogsQuery.error).message}</Alert>
          ) : catalogs.length > 0 ? (
            <ul className="flex flex-wrap gap-2">
              {catalogs.map((c) => (
                <li key={c.id}>
                  <Button
                    size="sm"
                    variant={selectedCatalogId === c.id ? 'primary' : 'outline'}
                    aria-pressed={selectedCatalogId === c.id}
                    onClick={() => setSelectedCatalogId(c.id)}
                  >
                    {c.name} ({c.code})
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500">Aucun catalogue de coûts.</p>
          )}
        </CardContent>

        <Dialog open={creating} onClose={() => setCreating(false)} title="Nouveau catalogue">
          <div className="space-y-4">
            {createCatalog.isError ? (
              <Alert variant="error" details={toDisplayError(createCatalog.error).details}>
                {toDisplayError(createCatalog.error).message}
              </Alert>
            ) : null}
            <CodeNameForm
              idPrefix="catalog-create"
              pending={createCatalog.isPending}
              onCancel={() => setCreating(false)}
              onSubmit={(values) =>
                createCatalog.mutate(values, { onSuccess: () => setCreating(false) })
              }
            />
          </div>
        </Dialog>
      </Card>

      {selectedCatalog ? <VersionsCard catalog={selectedCatalog} /> : null}
    </div>
  );
}

function VersionsCard({ catalog }: { catalog: CostCatalogDto }): JSX.Element {
  const versionsQuery = useCatalogVersions(catalog.id);
  const createVersion = useCreateCatalogVersion(catalog.id);
  const [creating, setCreating] = useState(false);
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [localError, setLocalError] = useState('');
  const [selectedVersionId, setSelectedVersionId] = useState('');

  const versions = versionsQuery.data ?? [];
  const selectedVersion = versions.find((v) => v.id === selectedVersionId) ?? null;

  const submit = () => {
    if (!validFrom) {
      setLocalError('La date de début de validité est requise.');
      return;
    }
    setLocalError('');
    createVersion.mutate(
      { validFrom, validTo: validTo || undefined },
      {
        onSuccess: () => {
          setValidFrom('');
          setValidTo('');
          setCreating(false);
        },
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>Versions — {catalog.name}</CardTitle>
          <Button
            size="sm"
            onClick={() => {
              createVersion.reset();
              setCreating(true);
            }}
          >
            Nouvelle version
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {versionsQuery.isLoading ? (
          <Spinner label="Chargement des versions…" />
        ) : versionsQuery.isError ? (
          <Alert variant="error">{toDisplayError(versionsQuery.error).message}</Alert>
        ) : versions.length > 0 ? (
          <ul className="flex flex-wrap gap-2">
            {versions.map((v) => (
              <li key={v.id}>
                <Button
                  size="sm"
                  variant={selectedVersionId === v.id ? 'primary' : 'outline'}
                  aria-pressed={selectedVersionId === v.id}
                  onClick={() => setSelectedVersionId(v.id)}
                >
                  v{v.versionNumber ?? '?'} · {CATALOG_VERSION_STATUS_LABELS[v.status]}
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500">Aucune version pour ce catalogue.</p>
        )}

        {selectedVersion ? <VersionDetail version={selectedVersion} /> : null}
      </CardContent>

      <Dialog open={creating} onClose={() => setCreating(false)} title="Nouvelle version">
        <div className="space-y-4">
          {createVersion.isError ? (
            <Alert variant="error" details={toDisplayError(createVersion.error).details}>
              {toDisplayError(createVersion.error).message}
            </Alert>
          ) : null}
          <div>
            <Label htmlFor="version-validFrom">Valide à partir du</Label>
            <Input
              id="version-validFrom"
              type="date"
              value={validFrom}
              aria-invalid={Boolean(localError)}
              onChange={(e) => setValidFrom(e.target.value)}
            />
            {localError ? <p className="mt-1 text-sm text-red-600">{localError}</p> : null}
          </div>
          <div>
            <Label htmlFor="version-validTo">Valide jusqu’au (facultatif)</Label>
            <Input
              id="version-validTo"
              type="date"
              value={validTo}
              onChange={(e) => setValidTo(e.target.value)}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setCreating(false)}
              disabled={createVersion.isPending}
            >
              Annuler
            </Button>
            <Button type="button" onClick={submit} loading={createVersion.isPending}>
              Créer la version
            </Button>
          </div>
        </div>
      </Dialog>
    </Card>
  );
}

function VersionDetail({ version }: { version: CatalogVersionDto }): JSX.Element {
  const transition = useTransitionCatalogVersion(version.id);

  return (
    <div className="space-y-4 rounded-md border border-slate-200 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-slate-800">
          Version v{version.versionNumber ?? '?'}
        </span>
        <Badge tone={badgeToneForStatus(version.status)}>
          {CATALOG_VERSION_STATUS_LABELS[version.status]}
        </Badge>
        <span className="text-xs text-slate-500">
          À partir du {version.validFrom}
          {version.validTo ? ` — jusqu’au ${version.validTo}` : ''}
        </span>
      </div>

      {/* Lifecycle: server enforces four-eyes; its 403/409 is rendered verbatim. */}
      <LifecycleButtons
        entityLabel={`la version v${version.versionNumber ?? ''}`}
        pending={transition.isPending}
        error={transition.isError ? transition.error : null}
        onAction={(action) => transition.mutate(action)}
      />

      <UnitCostsSection versionId={version.id} />
      <DuplicationPanel targetVersionId={version.id} />
    </div>
  );
}

function UnitCostsSection({ versionId }: { versionId: string }): JSX.Element {
  const componentTypesQuery = useComponentTypes();
  const unitsQuery = useReferentialMeasurementUnits();
  const costsQuery = useUnitCosts(versionId, {});
  const createCost = useCreateUnitCost(versionId);
  const [adding, setAdding] = useState(false);

  const componentTypes = componentTypesQuery.data ?? [];
  const units = unitsQuery.data ?? [];
  const typeById = new Map(componentTypes.map((t) => [t.id, t]));
  const unitById = new Map(units.map((u) => [u.id, u]));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h5 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Coûts unitaires
        </h5>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            createCost.reset();
            setAdding(true);
          }}
        >
          Ajouter un coût
        </Button>
      </div>

      {costsQuery.isLoading ? (
        <Spinner label="Chargement des coûts…" />
      ) : costsQuery.isError ? (
        <Alert variant="error">{toDisplayError(costsQuery.error).message}</Alert>
      ) : costsQuery.data && costsQuery.data.length > 0 ? (
        <ul className="divide-y divide-slate-100">
          {costsQuery.data.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
              <span className="font-medium text-slate-800">
                {typeById.get(c.componentTypeId)?.name ?? c.componentTypeId}
              </span>
              <Badge tone="neutral">{GEO_SCOPE_TYPE_LABELS[c.scopeType]}</Badge>
              <span className="font-semibold">{c.amount}</span>
              <span className="text-slate-500">
                / {unitById.get(c.measurementUnitId)?.code ?? c.measurementUnitId}
              </span>
              <span className="ml-auto text-xs text-slate-400">dès le {c.validFrom}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-slate-500">Aucun coût unitaire dans cette version.</p>
      )}

      <AddUnitCostDialog
        open={adding}
        onClose={() => setAdding(false)}
        componentTypes={componentTypes}
        units={units}
        pending={createCost.isPending}
        error={createCost.isError ? createCost.error : null}
        onSubmit={(input) => createCost.mutate(input, { onSuccess: () => setAdding(false) })}
      />
    </div>
  );
}

function AddUnitCostDialog({
  open,
  onClose,
  componentTypes,
  units,
  pending,
  error,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  componentTypes: { id: string; code: string; name: string }[];
  units: { id: string; code: string; name: string }[];
  pending: boolean;
  error: unknown;
  onSubmit: (input: UnitCostCreateInput) => void;
}): JSX.Element {
  const [scope, setScope] = useState<ScopeValue>(EMPTY_SCOPE);
  const [componentTypeId, setComponentTypeId] = useState('');
  const [measurementUnitId, setMeasurementUnitId] = useState('');
  const [amount, setAmount] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [source, setSource] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const submit = () => {
    const next: Record<string, string> = {};
    if (!scope.scopeType || !scope.scopeId)
      next.scope = 'Veuillez sélectionner une portée complète.';
    if (!componentTypeId) next.componentTypeId = 'Veuillez choisir un type de composant.';
    if (!measurementUnitId) next.measurementUnitId = 'Veuillez choisir une unité.';
    if (!amount.trim() || !/^\d+([.,]\d+)?$/.test(amount.trim()))
      next.amount = 'Montant invalide (nombre positif attendu).';
    if (!validFrom) next.validFrom = 'La date de début est requise.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    onSubmit({
      scopeType: scope.scopeType as UnitCostCreateInput['scopeType'],
      scopeId: scope.scopeId,
      componentTypeId,
      measurementUnitId,
      amount: amount.trim().replace(',', '.'),
      validFrom,
      source: source.trim() || undefined,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} title="Ajouter un coût unitaire">
      <div className="space-y-4">
        {error ? (
          <Alert variant="error" details={toDisplayError(error).details}>
            {toDisplayError(error).message}
          </Alert>
        ) : null}

        <ScopePicker idPrefix="unit-cost" value={scope} onChange={setScope} />
        {errors.scope ? <p className="text-sm text-red-600">{errors.scope}</p> : null}

        <div>
          <Label htmlFor="unit-cost-type">Type de composant</Label>
          <Select
            id="unit-cost-type"
            value={componentTypeId}
            onChange={(e) => setComponentTypeId(e.target.value)}
          >
            <option value="">— Choisir —</option>
            {componentTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.code})
              </option>
            ))}
          </Select>
          {errors.componentTypeId ? (
            <p className="mt-1 text-sm text-red-600">{errors.componentTypeId}</p>
          ) : null}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="unit-cost-unit">Unité de mesure</Label>
            <Select
              id="unit-cost-unit"
              value={measurementUnitId}
              onChange={(e) => setMeasurementUnitId(e.target.value)}
            >
              <option value="">— Choisir —</option>
              {units.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.code} — {u.name}
                </option>
              ))}
            </Select>
            {errors.measurementUnitId ? (
              <p className="mt-1 text-sm text-red-600">{errors.measurementUnitId}</p>
            ) : null}
          </div>
          <div>
            <Label htmlFor="unit-cost-amount">Montant (FCFA)</Label>
            <Input
              id="unit-cost-amount"
              inputMode="decimal"
              value={amount}
              aria-invalid={Boolean(errors.amount)}
              onChange={(e) => setAmount(e.target.value)}
            />
            {errors.amount ? <p className="mt-1 text-sm text-red-600">{errors.amount}</p> : null}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="unit-cost-validFrom">Valide à partir du</Label>
            <Input
              id="unit-cost-validFrom"
              type="date"
              value={validFrom}
              aria-invalid={Boolean(errors.validFrom)}
              onChange={(e) => setValidFrom(e.target.value)}
            />
            {errors.validFrom ? (
              <p className="mt-1 text-sm text-red-600">{errors.validFrom}</p>
            ) : null}
          </div>
          <div>
            <Label htmlFor="unit-cost-source">Source (facultatif)</Label>
            <Input
              id="unit-cost-source"
              value={source}
              onChange={(e) => setSource(e.target.value)}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={pending}>
            Annuler
          </Button>
          <Button type="button" onClick={submit} loading={pending}>
            Ajouter le coût
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

/**
 * Duplication tarifaire : la prévisualisation est OBLIGATOIRE avant toute
 * application. Le bouton « Appliquer » reste désactivé tant qu’aucune
 * prévisualisation propre (sans conflit) n’a été obtenue — les conflits sont
 * affichés et bloquent l’application.
 */
function DuplicationPanel({ targetVersionId }: { targetVersionId: string }): JSX.Element {
  const preview = usePreviewDuplication();
  const apply = useApplyDuplication();
  const [targetScope, setTargetScope] = useState<ScopeValue>(EMPTY_SCOPE);
  const [variationType, setVariationType] =
    useState<(typeof TariffVariationTypeSchema.options)[number]>('PERCENT');
  const [variationValue, setVariationValue] = useState('');
  const [localError, setLocalError] = useState('');

  const buildInput = (): DuplicationInput | null => {
    if (!targetScope.scopeType || !targetScope.scopeId) {
      setLocalError('Veuillez définir la portée cible.');
      return null;
    }
    if (!variationValue.trim()) {
      setLocalError('Veuillez saisir une valeur de variation.');
      return null;
    }
    setLocalError('');
    return {
      targetVersionId,
      source: {},
      target: {
        scopeType: targetScope.scopeType as DuplicationInput['target']['scopeType'],
        scopeId: targetScope.scopeId,
      },
      variation: { type: variationType, value: variationValue.trim().replace(',', '.') },
    };
  };

  const conflicts = preview.data?.conflicts ?? [];
  const creatable = preview.data?.creatable ?? [];
  // Apply is gated on a successful preview AND zero conflicts (conflicts block).
  const canApply = preview.isSuccess && conflicts.length === 0;

  const onPreview = () => {
    apply.reset();
    const input = buildInput();
    if (input) preview.mutate(input);
  };

  const onApply = () => {
    const input = buildInput();
    if (input) apply.mutate(input);
  };

  return (
    <div className="space-y-3 rounded-md border border-dashed border-slate-300 p-3">
      <h5 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
        Dupliquer des coûts
      </h5>

      <ScopePicker
        idPrefix="dup-target"
        value={targetScope}
        onChange={(next) => {
          setTargetScope(next);
          preview.reset();
          apply.reset();
        }}
        typeLabel="Portée cible"
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="dup-variation-type">Variation</Label>
          <Select
            id="dup-variation-type"
            value={variationType}
            onChange={(e) => {
              setVariationType(e.target.value as typeof variationType);
              preview.reset();
              apply.reset();
            }}
          >
            {TariffVariationTypeSchema.options.map((t) => (
              <option key={t} value={t}>
                {TARIFF_VARIATION_TYPE_LABELS[t]}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <Label htmlFor="dup-variation-value">Valeur</Label>
          <Input
            id="dup-variation-value"
            inputMode="decimal"
            value={variationValue}
            onChange={(e) => {
              setVariationValue(e.target.value);
              preview.reset();
              apply.reset();
            }}
          />
        </div>
      </div>

      {localError ? <p className="text-sm text-red-600">{localError}</p> : null}
      {preview.isError ? (
        <Alert variant="error" details={toDisplayError(preview.error).details}>
          {toDisplayError(preview.error).message}
        </Alert>
      ) : null}
      {apply.isError ? (
        <Alert variant="error" details={toDisplayError(apply.error).details}>
          {toDisplayError(apply.error).message}
        </Alert>
      ) : null}

      {preview.isSuccess ? (
        <div className="space-y-2 rounded-md bg-slate-50 p-2 text-sm">
          <p className="text-slate-700">
            {creatable.length} ligne(s) à créer, {conflicts.length} conflit(s).
          </p>
          {conflicts.length > 0 ? (
            <Alert variant="warning">
              Des conflits empêchent l’application. Résolvez-les avant de dupliquer.
              <ul className="mt-2 list-disc pl-5">
                {conflicts.map((c, i) => (
                  <li key={i}>{conflictLabel(c)}</li>
                ))}
              </ul>
            </Alert>
          ) : null}
          {apply.isSuccess ? (
            <Alert variant="success">
              Duplication appliquée : {apply.data?.createdCount ?? creatable.length} coût(s)
              créé(s).
            </Alert>
          ) : null}
        </div>
      ) : null}

      <div className="flex gap-2">
        <Button type="button" variant="secondary" onClick={onPreview} loading={preview.isPending}>
          Prévisualiser
        </Button>
        <Button
          type="button"
          onClick={onApply}
          loading={apply.isPending}
          disabled={!canApply}
          title={!canApply ? 'Une prévisualisation sans conflit est requise.' : undefined}
        >
          Appliquer
        </Button>
      </div>
    </div>
  );
}

function conflictLabel(line: DuplicationLineDto): string {
  const label = line.componentTypeCode ?? line.componentTypeId ?? 'Ligne';
  const reason = line.message ?? line.reason ?? 'conflit';
  return `${label} — ${reason}`;
}

// Silence unused-import warning for BulkAssignmentInput (kept for future bulk UI).
export type { BulkAssignmentInput };
